// Shared map pixel ↔ world coordinate math.
//
// A ROS map YAML gives `resolution` (metres per pixel) and `origin` ([x, y, z] —
// the world coordinate of the map image's BOTTOM-LEFT corner). Canvas pixels
// count from the TOP-left, hence the `height - …` flip in both directions.
//
// This logic was previously copy-pasted into PgmViewer, FleetLiveMap,
// LiveRobotPositionViewer, LocationTrackerPage and MapCanvas — and only
// PgmViewer had the inverse at all. New code should import from here.

export const DEFAULT_RESOLUTION = 0.05;
export const DEFAULT_ORIGIN = [0, 0, 0];

const readCfg = (mapConfig) => ({
  resolution: mapConfig?.resolution || DEFAULT_RESOLUTION,
  origin: mapConfig?.origin || DEFAULT_ORIGIN,
});

/**
 * World metres → canvas pixels.
 * @param {number} wx @param {number} wy
 * @param {{resolution:number, origin:number[]}} mapConfig
 * @param {number} height  map height in pixels
 * @param {{clampTo?:{width:number,height:number}}} [opts]
 */
export function worldToPx(wx, wy, mapConfig, height, opts = {}) {
  const { resolution, origin } = readCfg(mapConfig);
  let x = (wx - origin[0]) / resolution;
  let y = height - (wy - origin[1]) / resolution;

  // Live-tracking views clamp so an off-map robot still renders at the edge
  // instead of vanishing; the editors deliberately don't, so an out-of-bounds
  // click stays visibly wrong.
  const c = opts.clampTo;
  if (c) {
    x = Math.max(0, Math.min(x, c.width));
    y = Math.max(0, Math.min(y, c.height));
  }
  return { x, y };
}

/**
 * Canvas pixels → world metres. The inverse of worldToPx.
 * @param {number} px @param {number} py
 * @param {{resolution:number, origin:number[]}} mapConfig
 * @param {number} height  map height in pixels
 */
export function pxToWorld(px, py, mapConfig, height) {
  const { resolution, origin } = readCfg(mapConfig);
  return {
    x: px * resolution + origin[0],
    y: (height - py) * resolution + origin[1],
    z: origin[2] || 0,
  };
}

// Yaw helpers. Stored/transmitted yaw is ALWAYS radians (ROS convention: 0 = +X
// / East, counter-clockwise positive). Degrees exist only for the UI, where
// operators think in whole degrees and a rounded radian reads as noise.
export const degToRad = (deg) => (deg * Math.PI) / 180;
export const radToDeg = (rad) => (rad * 180) / Math.PI;

/** Radians → the 0–359 integer degrees the pickers display. */
export const radToDisplayDeg = (rad) => {
  const d = Math.round(radToDeg(rad || 0));
  return ((d % 360) + 360) % 360;
};

/**
 * Angle of the vector from a pin to the cursor, as 0–359 degrees.
 * `dy` is flipped because canvas Y grows downward while yaw grows CCW.
 */
export function angleFromPointer(originPx, originPy, pointerPx, pointerPy) {
  const dx = pointerPx - originPx;
  const dy = originPy - pointerPy;
  const deg = radToDeg(Math.atan2(dy, dx));
  return Math.round(((deg % 360) + 360) % 360);
}

/**
 * Draw a pose marker (dot, plus a heading arrow when asked for) on a canvas.
 * Shared by the admin and user initial-position pickers so the saved pose looks
 * identical in both portals.
 *
 * `showHeading` defaults to false: read-only map, navigation and patrol views
 * show the pin only. The yaw is still stored on the checkpoint and sent to the
 * robot — it is just not drawn. The pose editors, where dragging the arrow is
 * how the operator sets yaw, opt back in with `showHeading: true`.
 */
export function drawPoseMarker(
  ctx,
  px,
  py,
  yawRad,
  { color = "#f59e0b", arrowLen = 30, label, showHeading = false } = {}
) {
  ctx.save();

  // Heading arrow — editors only; see `showHeading` in the docblock above.
  if (showHeading) {
    const endX = px + arrowLen * Math.cos(yawRad);
    const endY = py - arrowLen * Math.sin(yawRad);

    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - 9 * Math.cos(yawRad - Math.PI / 6),
      endY + 9 * Math.sin(yawRad - Math.PI / 6)
    );
    ctx.lineTo(
      endX - 9 * Math.cos(yawRad + Math.PI / 6),
      endY + 9 * Math.sin(yawRad + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
  }

  // Body: filled disc with a white ring so it reads on both dark and light map pixels
  ctx.beginPath();
  ctx.arc(px, py, 8, 0, 2 * Math.PI);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = "#fff";
  ctx.stroke();

  if (label) {
    ctx.font = "600 12px Inter, Arial, sans-serif";
    const textW = ctx.measureText(label).width;
    const pillW = textW + 10;
    const pillH = 18;

    // Default: pill sits above-right of the dot, offset from its edge (the
    // gap avoids overlapping the dot/heading arrow). Flip to whichever side
    // has room, then clamp fully inside the canvas — so a pin near any edge
    // never draws its label off-canvas.
    const { width: canvasW, height: canvasH } = ctx.canvas;
    const gap = 4;
    const fitsRight = px + gap + pillW <= canvasW;
    const fitsAbove = py - gap - pillH >= 0;

    const rawX = fitsRight ? px + gap : px - gap - pillW;
    const rawY = fitsAbove ? py - gap - pillH : py + gap;
    const pillX = Math.min(Math.max(rawX, 0), Math.max(canvasW - pillW, 0));
    const pillY = Math.min(Math.max(rawY, 0), Math.max(canvasH - pillH, 0));

    // A short leader line ties the label to its dot even when neighboring
    // pins are close enough for labels to sit near each other.
    const leaderX = fitsRight ? pillX : pillX + pillW;
    ctx.strokeStyle = "rgba(0,0,0,0.72)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(leaderX, pillY + pillH / 2);
    ctx.stroke();

    ctx.fillStyle = "rgba(0,0,0,0.72)";
    ctx.fillRect(pillX, pillY, pillW, pillH);
    ctx.fillStyle = "#fff";
    ctx.fillText(label, pillX + 5, pillY + pillH - 5);
  }

  ctx.restore();
}
